import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, Phone, User } from 'lucide-react';
import { sendLeadToWebhook } from '../utils/webhook';
import { reachGoal } from '../utils/metrika';
export type ModalVariant = 'info' | 'manager' | 'expert' | 'report';
const variantConfig: Record<ModalVariant, {
  title: string;
  subtitle: string;
  button: string;
}> = {
  info: {
    title: 'Узнать подробнее об услуге',
    subtitle: 'Оставьте контакты — менеджер позвонит и подробно расскажет, как устроена экспертная оценка медицинской документации.',
    button: 'Узнать подробнее'
  },
  manager: {
    title: 'Пообщаться с менеджером',
    subtitle: 'Оставьте контакты — мы перезвоним в течение 30 минут в рабочее время и ответим на вопросы.',
    button: 'Заказать звонок'
  },
  expert: {
    title: 'Заказать экспертную оценку',
    subtitle: 'Оставьте контакты — менеджер свяжется, объяснит, какие документы нужны, и согласует удобное время.',
    button: 'Оставить заявку'
  },
  report: {
    title: 'Получить заключение',
    subtitle: 'Оставьте контакты — мы пришлём список необходимых материалов и согласуем порядок работы.',
    button: 'Оставить заявку'
  }
};
interface Props {
  variant: ModalVariant | null;
  onClose: () => void;
}
// Format raw input as +7 (XXX) XXX-XX-XX
function formatRuPhone(input: string): string {
  // Strip all non-digits
  let digits = input.replace(/\D/g, '');
  // If user typed leading 8, normalize to 7. If first digit isn't 7, prepend it.
  if (digits.length > 0) {
    if (digits[0] === '8') digits = '7' + digits.slice(1);
    if (digits[0] !== '7') digits = '7' + digits;
  }
  // Cap at 11 digits total (7 + 10)
  digits = digits.slice(0, 11);
  if (digits.length === 0) return '';
  const country = digits[0]; // always '7'
  const a = digits.slice(1, 4);
  const b = digits.slice(4, 7);
  const c = digits.slice(7, 9);
  const d = digits.slice(9, 11);
  let out = `+${country}`;
  if (a) out += ` (${a}`;
  if (a.length === 3) out += ')';
  if (b) out += ` ${b}`;
  if (c) out += `-${c}`;
  if (d) out += `-${d}`;
  return out;
}
function countPhoneDigits(value: string): number {
  return value.replace(/\D/g, '').length;
}
export const ContactModal: React.FC<Props> = ({
  variant,
  onClose
}) => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const isOpen = variant !== null;
  const config = variant ? variantConfig[variant] : null;
  const phoneDigits = countPhoneDigits(phone);
  const isPhoneValid = phoneDigits === 11;
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);
  useEffect(() => {
    if (!isOpen) {
      const t = setTimeout(() => {
        setName('');
        setPhone('');
        setSubmitted(false);
        setConsent(true);
        setSending(false);
      }, 300);
      return () => clearTimeout(t);
    }
  }, [isOpen]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isPhoneValid || !consent || sending) return;
    setSending(true);
    try {
      await sendLeadToWebhook({
        name: name.trim(),
        phone: phone.trim(),
        variant: variant || 'unknown',
        variantTitle: config?.title || ''
      });
    } catch {
      // already logged inside util; don't block UX
    }
    // Fire Yandex Metrika goal on every successful lead submission.
    reachGoal('lead', {
      form_variant: variant || 'unknown',
      form_title: config?.title || ''
    });
    setSending(false);
    setSubmitted(true);
  };
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhone(formatRuPhone(e.target.value));
  };
  const handlePhoneFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!phone) {
      setPhone('+7 (');
      // place caret at end after state updates
      requestAnimationFrame(() => {
        const el = e.target;
        const len = el.value.length;
        try {
          el.setSelectionRange(len, len);
        } catch {}
      });
    }
  };
  const handlePhoneBlur = () => {
    // If only the prefix is left, clear so placeholder shows again
    if (countPhoneDigits(phone) <= 1) setPhone('');
  };
  return <AnimatePresence data-id="element-6">
      {isOpen && config && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6 bg-navy/40 backdrop-blur-sm" onClick={onClose} data-id="element-7">
          <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.96
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.96
      }} transition={{
        duration: 0.3,
        ease: [0.21, 0.47, 0.32, 0.98]
      }} className="bg-offwhite rounded-3xl shadow-2xl w-full max-w-md relative overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-8">
            <button onClick={onClose} className="absolute top-5 right-5 w-9 h-9 rounded-full bg-beige/60 hover:bg-beige flex items-center justify-center text-navy transition-colors z-10" aria-label="Закрыть" data-id="element-9">
              <X size={18} data-id="element-10" />
            </button>

            {!submitted ? <div className="p-8 md:p-10" data-id="element-11">
                <h3 className="font-serif text-2xl md:text-3xl text-navy mb-3 pr-10 leading-tight" data-id="element-12">
                  {config.title}
                </h3>
                <p className="text-navyLight text-sm md:text-base mb-7 leading-relaxed" data-id="element-13">
                  {config.subtitle}
                </p>

                <form onSubmit={handleSubmit} className="space-y-4" data-id="element-14">
                  <div data-id="element-15">
                    <label className="block text-xs font-medium text-navyLight uppercase tracking-wider mb-2" data-id="element-16">
                      Ваше имя{' '}
                      <span className="normal-case tracking-normal text-navyLight/60" data-id="element-17">
                        (необязательно)
                      </span>
                    </label>
                    <div className="relative" data-id="element-18">
                      <User size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-navyLight/50" data-id="element-19" />
                      <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Иван Иванов" className="w-full pl-11 pr-4 py-3.5 bg-white border border-beige rounded-xl text-navy placeholder:text-navyLight/40 focus:outline-none focus:border-sage focus:ring-2 focus:ring-sage/20 transition-all" data-id="element-20" />
                    </div>
                  </div>

                  <div data-id="element-21">
                    <label className="block text-xs font-medium text-navyLight uppercase tracking-wider mb-2" data-id="element-22">
                      Телефон
                    </label>
                    <div className="relative" data-id="element-23">
                      <Phone size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-navyLight/50" data-id="element-24" />
                      <input type="tel" required inputMode="tel" autoComplete="tel" value={phone} onChange={handlePhoneChange} onFocus={handlePhoneFocus} onBlur={handlePhoneBlur} placeholder="+7 (___) ___-__-__" className="w-full pl-11 pr-4 py-3.5 bg-white border border-beige rounded-xl text-navy placeholder:text-navyLight/40 focus:outline-none focus:border-sage focus:ring-2 focus:ring-sage/20 transition-all" data-id="element-25" />
                    </div>
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer pt-2" data-id="element-26">
                    <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} className="mt-1 w-4 h-4 accent-sage shrink-0" data-id="element-27" />
                    <span className="text-xs text-navyLight leading-relaxed" data-id="element-28">
                      Согласен с{' '}
                      <a href="#/privacy" target="_blank" rel="noopener" className="text-sage underline hover:no-underline" data-id="element-29">
                        политикой обработки персональных данных
                      </a>
                      . Это информационная услуга, она не заменяет очной
                      консультации лечащего врача.
                    </span>
                  </label>

                  <button type="submit" disabled={!isPhoneValid || !consent || sending} className="w-full bg-sage hover:bg-sageDark disabled:bg-beige disabled:text-navyLight/50 disabled:cursor-not-allowed text-white py-4 rounded-xl font-medium transition-all duration-300 mt-2" data-id="element-30">
                    {sending ? 'Отправка…' : config.button}
                  </button>
                </form>
              </div> : <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.4
        }} className="p-10 text-center" data-id="element-31">
                <div className="w-16 h-16 rounded-full bg-sageLight flex items-center justify-center mx-auto mb-6" data-id="element-32">
                  <Check size={28} className="text-sage" strokeWidth={2.5} data-id="element-33" />
                </div>
                <h3 className="font-serif text-2xl text-navy mb-3" data-id="element-34">
                  Заявка принята
                </h3>
                <p className="text-navyLight leading-relaxed mb-6" data-id="element-35">
                  {name.trim() ? `Спасибо, ${name.trim().split(' ')[0]}. ` : 'Спасибо. '}
                  Менеджер свяжется с вами в течение 30 минут в рабочее время.
                </p>
                <button onClick={onClose} className="text-sage font-medium hover:text-sageDark transition-colors" data-id="element-36">
                  Закрыть
                </button>
              </motion.div>}
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
};